import { useState, useMemo } from 'react';
import { useAppStore } from '../stores';
import { loadChoreView, saveChoreView } from '../lib/choreViewPreference';
import {
  ChoreView, isMineOrUnassigned, calculateNextDue, getLastActivity, isChoreActive, filterAndSortChores, ScheduleKind, SCHEDULE_KIND_LABELS, scheduleKind,
  groupChoresByUrgency, countChoresDoneWithin, calendarDaysBetween, formatDueText, Urgency, URGENCY_ORDER, URGENCY_LABELS,
  householdAsSeenBy,
} from '../domain';
import { useHouseholdRoles } from './useHouseholdRoles';

export const useChoreList = () => {
  const { households: allHouseholds, loading, error } = useAppStore(state => state.chores);
  const userId = useAppStore(state => state.auth.user?.uid);
  const { childEverywhere } = useHouseholdRoles();
  // Where the user is a child, only the chores assigned to them: everything below starts from these.
  const households = useMemo(
    () => allHouseholds.map(h => householdAsSeenBy(h, userId)),
    [allHouseholds, userId]
  );
  // Not a filter: it's how the dashboard opens, so it's remembered and "Clear all" leaves it be.
  const [view, setViewState] = useState<ChoreView>(loadChoreView);
  const [selectedHousehold, setSelectedHousehold] = useState<string>('');
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [selectedScheduleKind, setSelectedScheduleKind] = useState<ScheduleKind | ''>('');
  const [searchText, setSearchText] = useState<string>('');
  /** Set by the summary tiles: shows one group only. */
  const [selectedUrgency, setSelectedUrgency] = useState<Urgency | ''>('');

  const items = useMemo(() => {
    if (!households) return [];
    return households.flatMap(h =>
      h.chores
        .filter(isChoreActive)
        .map(c => ({ ...c.chore, household_id: h.household.id, nextDue: calculateNextDue(c.chore, getLastActivity(c)) }))
    );
  }, [households]);

  const users = useMemo(() => {
    if (!households) return [];
    const userMap: Record<string, string> = {};
    households.forEach(h => {
      h.members?.forEach(m => {
        userMap[m.id] = m.name;
      });
    });
    return Object.entries(userMap).map(([id, name]) => ({ id, name }));
  }, [households]);

  // Until the user is known, every chore: an empty list would flash "Nothing for you" first. A
  // child everywhere has only their own chores already, and no switch to change it.
  const mineOnly = view === 'mine' && !!userId && !childEverywhere;

  const viewItems = useMemo(
    () => (mineOnly ? items.filter(chore => isMineOrUnassigned(chore, userId)) : items),
    [items, mineOnly, userId]
  );

  const filteredChores = useMemo(
    () => filterAndSortChores(viewItems, selectedHousehold, selectedAssignee, selectedLabels, searchText, selectedScheduleKind),
    [viewItems, selectedHousehold, selectedAssignee, selectedLabels, searchText, selectedScheduleKind]
  );

  // The tiles count what the other filters leave, so picking a tile never changes the numbers on it.
  const groups = useMemo(() => groupChoresByUrgency(filteredChores), [filteredChores]);

  const visibleGroups = useMemo(
    () => URGENCY_ORDER
      .filter(urgency => !selectedUrgency || urgency === selectedUrgency)
      .map(urgency => ({ urgency, label: URGENCY_LABELS[urgency], chores: groups[urgency] }))
      .filter(group => group.chores.length > 0),
    [groups, selectedUrgency]
  );

  // The chore to look forward to when nothing is due: the first of This week, else of Later.
  // The household's name is only given when there are several, since otherwise it says nothing.
  const nextUp = useMemo(() => {
    const next = groups.thisWeek[0] ?? groups.later[0];
    if (!next) return null;
    const now = new Date();
    return {
      id: next.id,
      name: next.name,
      dueText: formatDueText(calendarDaysBetween(now, next.nextDue), next.nextDue, now),
      householdName: (households?.length ?? 0) > 1
        ? households?.find(h => h.household.id === next.household_id)?.household.name ?? null
        : null,
    };
  }, [groups, households]);

  const doneThisWeek = useMemo(() => {
    const aggs = (households ?? [])
      .filter(h => !selectedHousehold || h.household.id === selectedHousehold)
      .flatMap(h => h.chores);
    // In your own view, the chores you did.
    return countChoresDoneWithin(aggs, 7, new Date(), mineOnly ? userId : undefined);
  }, [households, selectedHousehold, mineOnly, userId]);

  // Logic: Calculate available assignees based on selected household
  const availableAssignees = useMemo(() => {
    const choresToInspect = selectedHousehold
      ? items.filter(c => c.household_id === selectedHousehold)
      : items;

    const ids = new Set<string>();
    choresToInspect.forEach(chore => chore.assigned_to.forEach(a => ids.add(a.userId)));

    return Array.from(ids).map(id => ({
      id,
      name: users.find(u => u.id === id)?.name || id
    }));
  }, [items, selectedHousehold, users]);

  const availableLabels = useMemo(() => {
    const labelSet = new Set<string>();
    items.forEach(chore => (chore.labels ?? []).forEach(l => labelSet.add(l)));
    return Array.from(labelSet).sort();
  }, [items]);

  // Only the kinds some chore actually has, in the same order the schedule editor shows them.
  const availableScheduleKinds = useMemo(() => {
    const present = new Set(items.map(chore => scheduleKind(chore.schedule)));
    return (Object.keys(SCHEDULE_KIND_LABELS) as ScheduleKind[])
      .filter(kind => present.has(kind))
      .map(kind => ({ value: kind, label: SCHEDULE_KIND_LABELS[kind] }));
  }, [items]);

  const setView = (next: ChoreView) => {
    setViewState(next);
    saveChoreView(next);
  };

  const clearFilters = () => {
    setSelectedHousehold('');
    setSelectedAssignee('');
    setSelectedLabels([]);
    setSelectedScheduleKind('');
    setSearchText('');
    setSelectedUrgency('');
  };

  // The filters behind the Filters button, which the button counts; labels are out in the open.
  const panelFilterCount = (selectedAssignee ? 1 : 0) + (selectedScheduleKind ? 1 : 0);
  const hasFilters = !!(selectedHousehold || searchText.trim() || selectedUrgency || panelFilterCount || selectedLabels.length);

  return {
    groups: visibleGroups,
    counts: {
      overdue: groups.overdue.length,
      today: groups.today.length,
      thisWeek: groups.thisWeek.length,
      later: groups.later.length,
      doneThisWeek,
    },
    nextUp,
    households,
    availableAssignees,
    availableLabels,
    availableScheduleKinds,
    view,
    /** Whether "Mine / Everyone" and the assignee filter are offered: not to a child everywhere. */
    showWhose: !childEverywhere,
    childEverywhere,
    filters: { selectedHousehold, selectedAssignee, selectedLabels, selectedScheduleKind, searchText, selectedUrgency },
    selectedAssigneeName: availableAssignees.find(a => a.id === selectedAssignee)?.name ?? '',
    selectedScheduleLabel: selectedScheduleKind ? SCHEDULE_KIND_LABELS[selectedScheduleKind] : '',
    panelFilterCount,
    hasFilters,
    setHousehold: (id: string) => {
      setSelectedHousehold(id);
      setSelectedAssignee('');
    },
    // Picking someone looks at their chores, which your own view would cut down to the ones you share.
    setAssignee: (id: string) => {
      setSelectedAssignee(id);
      if (id && view === 'mine') setView('everyone');
    },
    setView: (next: ChoreView) => {
      setView(next);
      if (next === 'mine') setSelectedAssignee('');
    },
    toggleLabel: (label: string) => setSelectedLabels(current =>
      current.includes(label) ? current.filter(l => l !== label) : [...current, label]
    ),
    setScheduleKind: setSelectedScheduleKind,
    setSearchText,
    toggleUrgency: (urgency: Urgency) => setSelectedUrgency(current => (current === urgency ? '' : urgency)),
    clearFilters,
    loading,
    error,
    hasHouseholds: (households?.length ?? 0) > 0,
    isEmpty: items.length === 0,
    allCaughtUp: !hasFilters && viewItems.length > 0 && groups.overdue.length === 0 && groups.today.length === 0,
    noResults: items.length > 0 && visibleGroups.length === 0,
    /** Your own view is empty though others have chores, with nothing else narrowing it. */
    nothingForYou: mineOnly && !hasFilters && items.length > 0 && viewItems.length === 0,
  };
};
